/**
 * Everything on screen is rendered in the event's own timezone, whatever the phone is set to.
 * An attendee who flies in from another zone, or whose laptop is still on home time, must
 * never see a session an hour out — so no formatting here uses the device zone.
 */

export function formatTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone,
  }).format(new Date(iso));
}

/** The hour of day at the venue, for choosing between morning, afternoon and evening. */
export function hourIn(timeZone: string, now: Date): number {
  return Number(
    new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hourCycle: 'h23', timeZone }).format(now),
  );
}

export function formatTimeRange(startIso: string, endIso: string, timeZone: string): string {
  return `${formatTime(startIso, timeZone)} – ${formatTime(endIso, timeZone)}`;
}

export function formatDayHeading(calendarDate: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone,
  }).format(parseCalendarDate(calendarDate));
}

export function formatShortDay(calendarDate: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    timeZone,
  }).format(parseCalendarDate(calendarDate));
}

/** "Oct 1" — the second line of a day selector, under the weekday. */
export function formatMonthDay(calendarDate: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(parseCalendarDate(calendarDate));
}

/** "September 25" — a date written out inside a sentence. */
export function formatLongMonthDay(calendarDate: string, timeZone: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    timeZone,
  }).format(parseCalendarDate(calendarDate));
}

export function formatDateRange(startDate: string, endDate: string, timeZone: string): string {
  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();

  const startText = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone,
  }).format(start);
  const endText = new Intl.DateTimeFormat('en-US', {
    ...(sameMonth ? {} : { month: 'short' }),
    day: 'numeric',
    year: 'numeric',
    timeZone,
  }).format(end);

  return `${startText} – ${endText}`;
}

/**
 * "Today · 7:12 AM", then "Yesterday", then the weekday, then the date. An announcement is read
 * against the day it arrived rather than the clock: what matters is whether it is this morning's
 * news or Wednesday's, and the answer has to be the venue's day, not the phone's.
 */
export function formatFeedTimestamp(iso: string, timeZone: string, now: Date): string {
  const sentDate = calendarDateIn(timeZone, new Date(iso));
  const today = calendarDateIn(timeZone, now);
  const withinTheWeek = now.getTime() - new Date(iso).getTime() < 7 * 24 * 60 * 60 * 1000;

  const day =
    sentDate === today
      ? 'Today'
      : sentDate === shiftCalendarDate(today, -1)
        ? 'Yesterday'
        : withinTheWeek
          ? formatShortDay(sentDate, timeZone)
          : formatMonthDay(sentDate, timeZone);

  return `${day} · ${formatTime(iso, timeZone)}`;
}

/** A `date` column is a calendar concept, so it is anchored at UTC noon and never shifted. */
function parseCalendarDate(calendarDate: string): Date {
  return new Date(`${calendarDate}T12:00:00Z`);
}

/**
 * Days apart on the calendar, not 24-hour steps: subtracting a day from an instant lands in the
 * wrong day when the clocks change, and "yesterday" is exactly the label that would be wrong.
 */
function shiftCalendarDate(calendarDate: string, days: number): string {
  const shifted = parseCalendarDate(calendarDate);
  shifted.setUTCDate(shifted.getUTCDate() + days);
  return shifted.toISOString().slice(0, 10);
}

/** The calendar date it currently is at the venue, as YYYY-MM-DD. */
export function calendarDateIn(timeZone: string, now: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone,
  }).format(now);

  // en-CA already yields YYYY-MM-DD, which keeps this free of manual part juggling.
  return parts;
}

export interface Countdown {
  days: number;
  hours: number;
  minutes: number;
}

export function countdownTo(targetDate: string, timeZone: string, now: Date): Countdown {
  const target = startOfDayUtcFor(targetDate, timeZone);
  const diffMs = Math.max(0, target.getTime() - now.getTime());
  const totalMinutes = Math.floor(diffMs / 60_000);

  return {
    days: Math.floor(totalMinutes / (60 * 24)),
    hours: Math.floor((totalMinutes % (60 * 24)) / 60),
    minutes: totalMinutes % 60,
  };
}

/**
 * Midnight of a venue calendar date, as an instant. Built by measuring the zone's offset on
 * that date instead of hardcoding one, so it stays correct across a daylight-saving change.
 */
function startOfDayUtcFor(calendarDate: string, timeZone: string): Date {
  const naive = new Date(`${calendarDate}T00:00:00Z`);
  const formatted = new Intl.DateTimeFormat('en-US', {
    timeZone,
    timeZoneName: 'longOffset',
  }).format(naive);
  const match = /GMT([+-]\d{2}):(\d{2})/.exec(formatted);

  if (!match) {
    return naive;
  }

  const offsetMinutes = Number(match[1]) * 60 + Math.sign(Number(match[1])) * Number(match[2]);
  return new Date(naive.getTime() - offsetMinutes * 60_000);
}
